import React, { useEffect, useMemo, useState } from 'react';
import { Order, OrderLineItem, OrderProfit, getOrderProfit } from '../../api/orders';
import {
  BanType,
  createBansFromOrder,
} from '../../api/customerBans';
import {
  ManapoolOrderReportsResponse,
  ManapoolReportLine,
} from '../../api/manapoolOrders';
import { colors, spacing, typography, typographyFlat, borderRadius, colorExtensions } from '../../styles/tokens';
import { shortPickKindLabel } from '../../api/orderPull';
import { formatInstant } from '../../utils/datetime';
import { fulfillmentStatusLabel } from '../../utils/orderStatus';
import { ensureApiAuthenticated } from '../../services/api';
import {
  CombinePackageClient,
  CombinePackageSection,
} from './CombinePackageSection';

interface ReportApplyRow {
  key: string;
  reportId: string;
  lineId: string;
  quantity: number;
  restock: boolean;
  include: boolean;
  label: string;
}

interface OrderDetailsDrawerProps {
  order: Order | null;
  lineItems: OrderLineItem[];
  isOpen: boolean;
  isLoading: boolean;
  canMarkMissing?: boolean;
  canMarkReplaced?: boolean;
  canRetryShortfall?: boolean;
  canImportReports?: boolean;
  onMarkMissing?: (itemId: string, quantityFound: number, notes?: string) => Promise<void>;
  onMarkReplaced?: () => Promise<void>;
  onRetryShortfall?: (itemId: string) => Promise<void>;
  onFetchReports?: () => Promise<ManapoolOrderReportsResponse>;
  onApplyReports?: (payload: {
    report_id?: string | null;
    lines: Array<{ line_id: string; quantity: number; restock: boolean }>;
  }) => Promise<void>;
  onEstimateIncompleteCogs?: () => Promise<void>;
  onAfterUpdate?: () => void;
  onOrderUpdated?: (order: Order) => void;
  onOpenPartner?: (partnerOrderId: string) => void;
  /** Sample-data / test seam. Omitted in the live drawer. */
  combineClient?: CombinePackageClient;
  onClose: () => void;
}

function buildReportRows(
  reports: ManapoolOrderReportsResponse,
  lineItems: OrderLineItem[],
): ReportApplyRow[] {
  const rows: ReportApplyRow[] = [];
  const lines: ManapoolReportLine[] = reports.lines || [];
  if (lines.length > 0) {
    lines.forEach((line, idx) => {
      const matchedId = line.matched_order_item_id
        || (lineItems.length === 1 ? lineItems[0].id : '');
      const matchedItem = lineItems.find((li) => li.id === matchedId);
      rows.push({
        key: `${line.report_id}-${line.manapool_order_item_id || idx}`,
        reportId: line.report_id,
        lineId: matchedId || '',
        quantity: Math.max(1, Number(line.quantity) || 1),
        restock: false,
        include: Boolean(matchedId),
        label: matchedItem?.product_name
          || line.card_name
          || line.manapool_order_item_id
          || `Report line ${idx + 1}`,
      });
    });
    return rows;
  }
  // No report line items — fall back to SyncBase lines for manual apply.
  return lineItems.map((li) => ({
    key: li.id,
    reportId: '',
    lineId: li.id,
    quantity: Math.max(1, Number(li.allocated_qty) || Number(li.requested_qty) || 1),
    restock: false,
    include: false,
    label: li.product_name || li.external_line_id || li.id,
  }));
}

export const OrderDetailsDrawer: React.FC<OrderDetailsDrawerProps> = ({
  order,
  lineItems,
  isOpen,
  isLoading,
  canMarkMissing = false,
  canMarkReplaced = false,
  canRetryShortfall = false,
  canImportReports = false,
  onMarkMissing,
  onMarkReplaced,
  onRetryShortfall,
  onFetchReports,
  onApplyReports,
  onEstimateIncompleteCogs,
  onAfterUpdate,
  onOrderUpdated,
  onOpenPartner,
  combineClient,
  onClose
}) => {
  const [selectedLineItemId, setSelectedLineItemId] = useState<string | null>(null);
  const [orderProfit, setOrderProfit] = useState<OrderProfit | null>(null);
  const [profitError, setProfitError] = useState<string | null>(null);
  const [quantityFoundInput, setQuantityFoundInput] = useState<number>(0);
  const [missingNotes, setMissingNotes] = useState('');
  const [isSubmittingMissing, setIsSubmittingMissing] = useState(false);
  const [missingError, setMissingError] = useState<string | null>(null);
  const [isSubmittingReplaced, setIsSubmittingReplaced] = useState(false);
  const [replacedError, setReplacedError] = useState<string | null>(null);
  const [isSubmittingRetry, setIsSubmittingRetry] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [isFetchingReports, setIsFetchingReports] = useState(false);
  const [isApplyingReports, setIsApplyingReports] = useState(false);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [reportsSummary, setReportsSummary] = useState<string | null>(null);
  const [reportRows, setReportRows] = useState<ReportApplyRow[]>([]);
  const [activeReportId, setActiveReportId] = useState<string | null>(null);
  const [showBanPanel, setShowBanPanel] = useState(false);
  const [banTypes, setBanTypes] = useState<BanType[]>([]);
  const [banReason, setBanReason] = useState('');
  const [banBusy, setBanBusy] = useState(false);
  const [banError, setBanError] = useState<string | null>(null);
  const [banMessage, setBanMessage] = useState<string | null>(null);
  const [estimateBusy, setEstimateBusy] = useState(false);
  const [estimateError, setEstimateError] = useState<string | null>(null);
  const [estimateMessage, setEstimateMessage] = useState<string | null>(null);
  const [combineWide, setCombineWide] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!isOpen || !order) {
      setOrderProfit(null);
      setProfitError(null);
      return;
    }
    const hasLineProfit = lineItems.some((item) => item.cogs !== undefined && item.cogs !== null);
    if (hasLineProfit) {
      setOrderProfit(null);
      setProfitError(null);
      return;
    }
    (async () => {
      try {
        const profit = await getOrderProfit(order.id);
        if (!cancelled) {
          setOrderProfit(profit);
          setProfitError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setOrderProfit(null);
          // Non-admins get 403 — hide P&L quietly
          const status = (err as { response?: { status?: number } } | null)?.response?.status;
          setProfitError(status === 403 ? null : (err instanceof Error ? err.message : 'Failed to load profit'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, order, lineItems]);

  useEffect(() => {
    if (!isOpen) {
      setCombineWide(false);
    }
  }, [isOpen, order?.id]);

  const derivedProfit = useMemo(() => {
    if (!lineItems.length) return null;
    if (!lineItems.some((item) => item.cogs !== undefined && item.cogs !== null)) {
      return null;
    }
    const productRevenue = lineItems.reduce((sum, item) => sum + (parseFloat(String(item.line_total)) || 0), 0);
    const cogs = lineItems.reduce((sum, item) => sum + (Number(item.cogs) || 0), 0);
    const grossProfit = productRevenue - cogs;
    const fees = parseFloat(String(order?.fees || 0)) || 0;
    const complete = lineItems.every((item) => item.cogs_complete !== false);
    return {
      product_revenue: productRevenue,
      cogs,
      gross_profit: grossProfit,
      fees,
      fees_source: order?.fees_source || null,
      net_proceeds: order?.net_proceeds ?? null,
      net_profit: grossProfit - fees,
      gross_margin_pct: productRevenue > 0 ? (grossProfit / productRevenue) * 100 : null,
      cogs_complete: complete,
    };
  }, [lineItems, order]);

  const profitView = derivedProfit || orderProfit;

  const selectedLineItem = useMemo(() => {
    if (selectedLineItemId) {
      return lineItems.find((item) => item.id === selectedLineItemId) || null;
    }
    return lineItems[0] || null;
  }, [lineItems, selectedLineItemId]);

  if (!isOpen || !order) return null;

  const drawerExpanded = Boolean(selectedLineItem) || combineWide;

  const formatDate = (dateString: string) => formatInstant(dateString);

  const formatAmount = (amount?: string | number | null, emptyLabel = 'not reported') => {
    if (amount === undefined || amount === null || amount === '') {
      return emptyLabel;
    }
    const num = typeof amount === 'number' ? amount : parseFloat(amount);
    if (Number.isNaN(num)) {
      return emptyLabel;
    }
    return `$${num.toFixed(2)}`;
  };

  const formatFees = () => {
    const source = (order.fees_source || '').toLowerCase();
    if (source === 'unavailable') {
      return 'not reported';
    }
    const label = formatAmount(order.fees, '$0.00');
    if (source === 'estimated') {
      return `${label} (est.)`;
    }
    return label;
  };

  const formatShippingAddress = () => {
    const address = order.shipping_address_json;
    if (address?.formatted) {
      return address.formatted;
    }
    if (address) {
      const lines = [];
      if (address.name) lines.push(address.name);
      if (address.line1) lines.push(address.line1);
      if (address.line2) lines.push(address.line2);
      const locality = [address.city, address.state, address.postal_code].filter(Boolean).join(', ');
      if (locality) lines.push(locality);
      if (address.country) lines.push(address.country);
      if (lines.length > 0) return lines.join('\n');
    }
    return order.shipping_address;
  };

  const formatStatus = (status?: string) => fulfillmentStatusLabel(status, '—');

  const getSourceBadgeColor = (source: string) => {
    const colors_map: Record<string, { bg: string; text: string }> = {
      tcgplayer: { bg: `${colors.info}20`, text: colorExtensions.infoDark },
      shopify: { bg: `${colors.success}19`, text: colorExtensions.successDark },
      ebay: { bg: `${colors.warning}26`, text: colorExtensions.warningDark },
      manapool: { bg: `${colors.secondary}1A`, text: colors.secondary },
    };
    return colors_map[source.toLowerCase()] || { bg: colors.backgroundAlt, text: colors.text };
  };

  const sourceBadge = getSourceBadgeColor(order.source);

  const handleSelectLineItem = (item: OrderLineItem) => {
    setSelectedLineItemId(item.id);
    const defaultFound = Math.max(0, Math.min(item.requested_qty - 1, item.allocated_qty));
    setQuantityFoundInput(defaultFound);
    setMissingNotes('');
    setMissingError(null);
  };

  const handleMarkMissing = async () => {
    if (!selectedLineItem || !onMarkMissing) {
      return;
    }

    const quantityFound = Math.max(0, Math.min(selectedLineItem.requested_qty, quantityFoundInput || 0));
    if (quantityFound >= selectedLineItem.requested_qty) {
      setMissingError('Quantity found must be less than ordered quantity.');
      return;
    }

    setIsSubmittingMissing(true);
    setMissingError(null);
    try {
      await onMarkMissing(selectedLineItem.id, quantityFound, missingNotes || undefined);
      if (onAfterUpdate) {
        onAfterUpdate();
      }
    } catch (error) {
      setMissingError(error instanceof Error ? error.message : 'Failed to mark item missing');
    } finally {
      setIsSubmittingMissing(false);
    }
  };

  const handleRetryShortfall = async () => {
    if (!selectedLineItem || !onRetryShortfall) {
      return;
    }
    setIsSubmittingRetry(true);
    setRetryError(null);
    try {
      await onRetryShortfall(selectedLineItem.id);
      if (onAfterUpdate) {
        onAfterUpdate();
      }
    } catch (error) {
      setRetryError(error instanceof Error ? error.message : 'Failed to retry shortfall allocation');
    } finally {
      setIsSubmittingRetry(false);
    }
  };

  const formatCopyCurrency = (amount?: number | null) => {
    if (amount === undefined || amount === null) {
      return '—';
    }
    return `$${amount.toFixed(2)}`;
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: `${colors.text}4D`,
          zIndex: 999
        }}
      />

      {/* Drawer */}
      <div style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: drawerExpanded ? `calc(${spacing.lg} * 22)` : `calc(${spacing.lg} * 15)`,
        maxWidth: '90%',
        backgroundColor: colors.white,
        boxShadow: `-4px 0 20px ${colors.text}26`,
        zIndex: 1000,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: spacing.lg,
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: colors.white,
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          <div>
            <h2 style={{
              fontSize: typography.h2.fontSize,
              fontWeight: typography.h2.fontWeight,
              color: colors.text,
              margin: 0,
              marginBottom: spacing.xs
            }}>
              Order Details
            </h2>
            <div style={{
              fontSize: typography.small.fontSize,
              color: colors.textSecondary,
              fontFamily: 'monospace'
            }}>
              {order.display_id || order.external_id}
            </div>
            {order.is_repeat_customer && (
              <div style={{
                marginTop: spacing.xs,
                fontSize: typography.small.fontSize,
                color: colors.primaryDark,
              }}>
                Repeat customer
                {typeof order.prior_order_count === 'number'
                  ? ` · ${order.prior_order_count} prior order${order.prior_order_count === 1 ? '' : 's'}`
                  : ''}
              </div>
            )}
            {order.combined_with_order_id && (
              <div style={{ marginTop: spacing.xs }}>
                {onOpenPartner ? (
                  <button
                    type="button"
                    onClick={() => onOpenPartner(order.combined_with_order_id!)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      color: colors.primary,
                      fontSize: typography.small.fontSize,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontFamily: 'inherit',
                    }}
                  >
                    Combined with {order.combined_with_label || order.combined_with_order_id}
                  </button>
                ) : (
                  <div style={{ fontSize: typography.small.fontSize, color: colors.text }}>
                    Combined with {order.combined_with_label || order.combined_with_order_id}
                  </div>
                )}
                {order.combined_with_label && order.combined_with_label !== order.combined_with_order_id && (
                  <div style={{
                    fontFamily: 'monospace',
                    fontSize: typography.caption.fontSize,
                    color: colors.textSecondary,
                  }}>
                    {order.combined_with_order_id}
                  </div>
                )}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: typography.h1.fontSize,
              cursor: 'pointer',
              color: colors.textSecondary,
              padding: 0,
              width: spacing.xl,
              height: spacing.xl,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: spacing.lg, flex: 1 }}>
          {/* Order Metadata */}
          <div style={{
            backgroundColor: colors.backgroundAlt,
            border: `1px solid ${colors.border}`,
            borderRadius: borderRadius.lg,
            padding: spacing.lg,
            marginBottom: spacing.lg
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: spacing.lg
            }}>
              <div>
                <div style={{
                  fontSize: typography.small.fontSize,
                  color: colors.textSecondary,
                  marginBottom: spacing.xs
                }}>
                  Source
                </div>
                <span style={{
                  display: 'inline-block',
                  padding: `${spacing.xs} ${spacing.sm}`,
                  backgroundColor: sourceBadge.bg,
                  color: sourceBadge.text,
                  fontSize: typography.small.fontSize,
                  fontWeight: typographyFlat.fontWeight.medium,
                  borderRadius: borderRadius.sm,
                  textTransform: 'capitalize'
                }}>
                  {order.source}
                </span>
              </div>

              <div>
                <div style={{
                  fontSize: typography.small.fontSize,
                  color: colors.textSecondary,
                  marginBottom: spacing.xs
                }}>
                  Order Date
                </div>
                <div style={{
                  fontSize: typography.body.fontSize,
                  color: colors.text
                }}>
                  {formatDate(order.order_timestamp)}
                </div>
              </div>

              <div>
                <div style={{
                  fontSize: typography.small.fontSize,
                  color: colors.textSecondary,
                  marginBottom: spacing.xs
                }}>
                  Buyer Name
                </div>
                <div style={{
                  fontSize: typography.body.fontSize,
                  color: colors.text
                }}>
                  {order.buyer_name || <span style={{ color: colors.textSecondary }}>—</span>}
                </div>
              </div>

              <div>
                <div style={{
                  fontSize: typography.small.fontSize,
                  color: colors.textSecondary,
                  marginBottom: spacing.xs
                }}>
                  Buyer Email
                </div>
                <div style={{
                  fontSize: typography.body.fontSize,
                  color: colors.text
                }}>
                  {order.buyer_email || <span style={{ color: colors.textSecondary }}>—</span>}
                </div>
              </div>

              <div>
                <div style={{
                  fontSize: typography.small.fontSize,
                  color: colors.textSecondary,
                  marginBottom: spacing.xs
                }}>
                  Imported
                </div>
                <div style={{
                  fontSize: typography.body.fontSize,
                  color: colors.text
                }}>
                  {formatDate(order.created_at)}
                </div>
              </div>

              <div>
                <div style={{
                  fontSize: typography.small.fontSize,
                  color: colors.textSecondary,
                  marginBottom: spacing.xs
                }}>
                  Fulfillment
                </div>
                <div style={{
                  fontSize: typography.body.fontSize,
                  color: colors.text
                }}>
                  {formatStatus(order.fulfillment_status)}
                </div>
              </div>

              <div>
                <div style={{
                  fontSize: typography.small.fontSize,
                  color: colors.textSecondary,
                  marginBottom: spacing.xs
                }}>
                  Payment
                </div>
                <div style={{
                  fontSize: typography.body.fontSize,
                  color: colors.text
                }}>
                  {formatStatus(order.commercial_status)}
                </div>
              </div>
            </div>

            <div style={{
              marginTop: spacing.md,
              paddingTop: spacing.md,
              borderTop: `1px solid ${colors.border}`,
            }}>
              <div style={{
                fontSize: typography.small.fontSize,
                color: colors.textSecondary,
                marginBottom: spacing.xs,
              }}>
                Shipping Address
              </div>
              <div style={{
                fontSize: typography.body.fontSize,
                color: colors.text,
                whiteSpace: 'pre-wrap',
                lineHeight: 1.4,
              }}>
                {formatShippingAddress() || <span style={{ color: colors.textSecondary }}>—</span>}
              </div>
            </div>

            {order.ban_hold && order.hold_reason && (
              <div style={{
                marginTop: spacing.md,
                padding: spacing.md,
                backgroundColor: `${colors.error}14`,
                borderRadius: borderRadius.md,
                color: colors.error,
                fontSize: typography.small.fontSize,
              }}>
                {order.hold_reason}
              </div>
            )}

            {(order.short_picks?.length ?? 0) > 0 && (
              <div style={{
                marginTop: spacing.md,
                padding: spacing.md,
                backgroundColor: `${colors.warning}26`,
                borderRadius: borderRadius.md,
                color: colors.text,
                fontSize: typography.small.fontSize,
              }}>
                <div style={{
                  fontWeight: typographyFlat.fontWeight.semibold,
                  marginBottom: spacing.xs,
                }}>
                  Short pick
                </div>
                <ul style={{ margin: 0, paddingLeft: spacing.lg }}>
                  {order.short_picks?.map((pick) => (
                    <li key={pick.id}>
                      {shortPickKindLabel(pick.kind)} {pick.qty}
                      {pick.card_name ? ` · ${pick.card_name}` : ''}
                      {pick.slot_number != null ? ` · slot ${pick.slot_number}` : ''}
                      {pick.note ? ` · ${pick.note}` : ''}
                      {' · '}
                      {formatDate(pick.created_at)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div style={{
              marginTop: spacing.md,
              paddingTop: spacing.md,
              borderTop: `1px solid ${colors.border}`,
            }}>
              {!showBanPanel ? (
                <button
                  type="button"
                  onClick={() => {
                    const available: BanType[] = [];
                    if (order.buyer_name) available.push('name');
                    if (order.buyer_email) available.push('email');
                    if (order.shipping_address || order.shipping_address_json) available.push('address');
                    setBanTypes(available);
                    setBanReason('');
                    setBanError(null);
                    setBanMessage(null);
                    setShowBanPanel(true);
                  }}
                  style={{
                    padding: `${spacing.sm} ${spacing.md}`,
                    backgroundColor: colors.backgroundAlt,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                    borderRadius: borderRadius.sm,
                    cursor: 'pointer',
                    fontSize: typography.small.fontSize,
                    fontWeight: typographyFlat.fontWeight.semibold,
                  }}
                >
                  Add to ban list
                </button>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                  <div style={{
                    fontSize: typography.small.fontSize,
                    color: colors.textSecondary,
                  }}>
                    Ban customer fields from this order
                  </div>
                  <div style={{ display: 'flex', gap: spacing.md, flexWrap: 'wrap' }}>
                    {(['name', 'email', 'address'] as BanType[]).map((t) => {
                      const enabled =
                        (t === 'name' && Boolean(order.buyer_name)) ||
                        (t === 'email' && Boolean(order.buyer_email)) ||
                        (t === 'address' && Boolean(order.shipping_address || order.shipping_address_json));
                      return (
                        <label
                          key={t}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: spacing.xs,
                            fontSize: typography.small.fontSize,
                            color: enabled ? colors.text : colors.textSecondary,
                            textTransform: 'capitalize',
                          }}
                        >
                          <input
                            type="checkbox"
                            disabled={!enabled}
                            checked={banTypes.includes(t)}
                            onChange={() => {
                              setBanTypes((prev) =>
                                prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
                              );
                            }}
                          />
                          {t}
                        </label>
                      );
                    })}
                  </div>
                  <input
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                    placeholder="Reason (optional)"
                    style={{
                      padding: `${spacing.xs} ${spacing.sm}`,
                      border: `1px solid ${colors.border}`,
                      borderRadius: borderRadius.sm,
                      fontSize: typography.small.fontSize,
                    }}
                  />
                  {banError && (
                    <div style={{ color: colors.error, fontSize: typography.small.fontSize }}>
                      {banError}
                    </div>
                  )}
                  {banMessage && (
                    <div style={{ color: colors.success, fontSize: typography.small.fontSize }}>
                      {banMessage}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: spacing.sm }}>
                    <button
                      type="button"
                      disabled={banBusy || banTypes.length === 0}
                      onClick={async () => {
                        setBanBusy(true);
                        setBanError(null);
                        setBanMessage(null);
                        try {
                          await ensureApiAuthenticated();
                          const result = await createBansFromOrder({
                            order_id: order.id,
                            ban_types: banTypes,
                            reason: banReason || undefined,
                          });
                          setBanMessage(
                            `Created ${result.created.length} ban(s)` +
                              (result.skipped.length ? `; skipped ${result.skipped.length}` : ''),
                          );
                        } catch (err) {
                          setBanError(err instanceof Error ? err.message : 'Failed to add ban');
                        } finally {
                          setBanBusy(false);
                        }
                      }}
                      style={{
                        padding: `${spacing.sm} ${spacing.md}`,
                        backgroundColor: banBusy ? colors.backgroundAlt : colors.primary,
                        color: colors.white,
                        border: 'none',
                        borderRadius: borderRadius.sm,
                        cursor: banBusy ? 'default' : 'pointer',
                        fontSize: typography.small.fontSize,
                        fontWeight: typographyFlat.fontWeight.semibold,
                      }}
                    >
                      {banBusy ? 'Saving…' : 'Save bans'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowBanPanel(false)}
                      style={{
                        padding: `${spacing.sm} ${spacing.md}`,
                        backgroundColor: 'transparent',
                        color: colors.textSecondary,
                        border: `1px solid ${colors.border}`,
                        borderRadius: borderRadius.sm,
                        cursor: 'pointer',
                        fontSize: typography.small.fontSize,
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {canMarkReplaced && onMarkReplaced && (
              <div style={{
                marginTop: spacing.md,
                paddingTop: spacing.md,
                borderTop: `1px solid ${colors.border}`,
              }}>
                <div style={{
                  fontSize: typography.small.fontSize,
                  color: colors.textSecondary,
                  marginBottom: spacing.sm,
                }}>
                  Manapool shows this as Replaced? Mark shortfall and move to Completed (no restock).
                </div>
                {replacedError && (
                  <div style={{ color: colors.error, fontSize: typography.small.fontSize, marginBottom: spacing.sm }}>
                    {replacedError}
                  </div>
                )}
                <button
                  type="button"
                  disabled={isSubmittingReplaced}
                  onClick={async () => {
                    setReplacedError(null);
                    setIsSubmittingReplaced(true);
                    try {
                      await onMarkReplaced();
                      onAfterUpdate?.();
                    } catch (err) {
                      setReplacedError(err instanceof Error ? err.message : 'Failed to mark replaced');
                    } finally {
                      setIsSubmittingReplaced(false);
                    }
                  }}
                  style={{
                    padding: `${spacing.sm} ${spacing.md}`,
                    backgroundColor: isSubmittingReplaced ? colors.backgroundAlt : colors.warning,
                    color: colors.white,
                    border: 'none',
                    borderRadius: borderRadius.md,
                    cursor: isSubmittingReplaced ? 'not-allowed' : 'pointer',
                    fontSize: typography.small.fontSize,
                    fontWeight: typographyFlat.fontWeight.medium,
                  }}
                >
                  {isSubmittingReplaced ? 'Marking…' : 'Mark as Replaced (shortfall)'}
                </button>
              </div>
            )}

            {canImportReports && onFetchReports && onApplyReports && (
              <div style={{
                marginTop: spacing.md,
                paddingTop: spacing.md,
                borderTop: `1px solid ${colors.border}`,
              }}>
                <div style={{
                  fontSize: typography.small.fontSize,
                  color: colors.textSecondary,
                  marginBottom: spacing.sm,
                }}>
                  Post-ship or partial-line Manapool report (complaint, lost package, single-line
                  cancel). Fetch for this order, choose lines, and toggle restock per line.
                  Refresh Pending only auto-cancels when the report covers every line.
                </div>
                {reportsError && (
                  <div style={{ color: colors.error, fontSize: typography.small.fontSize, marginBottom: spacing.sm }}>
                    {reportsError}
                  </div>
                )}
                {reportsSummary && (
                  <div style={{
                    fontSize: typography.small.fontSize,
                    color: colors.text,
                    marginBottom: spacing.sm,
                    whiteSpace: 'pre-wrap',
                  }}>
                    {reportsSummary}
                  </div>
                )}
                <button
                  type="button"
                  disabled={isFetchingReports || isApplyingReports}
                  onClick={async () => {
                    setReportsError(null);
                    setIsFetchingReports(true);
                    try {
                      const data = await onFetchReports();
                      const rows = buildReportRows(data, lineItems);
                      setReportRows(rows);
                      setActiveReportId(rows.find((r) => r.reportId)?.reportId || null);
                      const firstLine = (data.lines || [])[0];
                      const method = firstLine?.proposed_remediation_method || data.detected_terminal_status || '—';
                      const comment = firstLine?.comment || '';
                      setReportsSummary(
                        `Reports: ${(data.lines || []).length} line(s)`
                        + ` · method=${method}`
                        + (comment ? `\n${comment}` : ''),
                      );
                    } catch (err) {
                      setReportsError(err instanceof Error ? err.message : 'Failed to fetch reports');
                      setReportRows([]);
                    } finally {
                      setIsFetchingReports(false);
                    }
                  }}
                  style={{
                    padding: `${spacing.sm} ${spacing.md}`,
                    backgroundColor: colors.backgroundAlt,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                    borderRadius: borderRadius.md,
                    cursor: isFetchingReports ? 'not-allowed' : 'pointer',
                    fontSize: typography.small.fontSize,
                    fontWeight: typographyFlat.fontWeight.medium,
                    marginBottom: spacing.sm,
                  }}
                >
                  {isFetchingReports ? 'Fetching…' : 'Fetch Manapool Reports'}
                </button>

                {reportRows.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                    {reportRows.map((row) => (
                      <div
                        key={row.key}
                        style={{
                          border: `1px solid ${colors.border}`,
                          borderRadius: borderRadius.md,
                          padding: spacing.sm,
                          fontSize: typography.small.fontSize,
                        }}
                      >
                        <label style={{ display: 'flex', alignItems: 'center', gap: spacing.xs, marginBottom: spacing.xs }}>
                          <input
                            type="checkbox"
                            checked={row.include}
                            onChange={(e) => {
                              const include = e.target.checked;
                              setReportRows((prev) => prev.map((r) => (
                                r.key === row.key ? { ...r, include } : r
                              )));
                            }}
                          />
                          <span>{row.label}</span>
                        </label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.sm, alignItems: 'center' }}>
                          <label>
                            Line
                            <select
                              value={row.lineId}
                              onChange={(e) => {
                                const lineId = e.target.value;
                                setReportRows((prev) => prev.map((r) => (
                                  r.key === row.key ? { ...r, lineId, include: Boolean(lineId) } : r
                                )));
                              }}
                              style={{ marginLeft: spacing.xs, maxWidth: 220 }}
                            >
                              <option value="">Select SyncBase line…</option>
                              {lineItems.map((li) => (
                                <option key={li.id} value={li.id}>
                                  {li.product_name || li.external_line_id || li.id}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            Qty
                            <input
                              type="number"
                              min={1}
                              value={row.quantity}
                              onChange={(e) => {
                                const quantity = Math.max(1, Number(e.target.value) || 1);
                                setReportRows((prev) => prev.map((r) => (
                                  r.key === row.key ? { ...r, quantity } : r
                                )));
                              }}
                              style={{ width: 64, marginLeft: spacing.xs }}
                            />
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                            <input
                              type="checkbox"
                              checked={row.restock}
                              onChange={(e) => {
                                const restock = e.target.checked;
                                setReportRows((prev) => prev.map((r) => (
                                  r.key === row.key ? { ...r, restock } : r
                                )));
                              }}
                            />
                            Restock inventory (unchecked = leave as sold / no shelf return)
                          </label>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      disabled={
                        isApplyingReports
                        || !reportRows.some((r) => r.include && r.lineId)
                      }
                      onClick={async () => {
                        setReportsError(null);
                        setIsApplyingReports(true);
                        try {
                          const lines = reportRows
                            .filter((r) => r.include && r.lineId)
                            .map((r) => ({
                              line_id: r.lineId,
                              quantity: r.quantity,
                              restock: r.restock,
                            }));
                          await onApplyReports({
                            report_id: activeReportId,
                            lines,
                          });
                          setReportsSummary((prev) => `${prev || ''}\nApplied ${lines.length} line(s).`);
                          onAfterUpdate?.();
                        } catch (err) {
                          setReportsError(err instanceof Error ? err.message : 'Failed to apply report');
                        } finally {
                          setIsApplyingReports(false);
                        }
                      }}
                      style={{
                        padding: `${spacing.sm} ${spacing.md}`,
                        backgroundColor: colors.primary,
                        color: colors.white,
                        border: 'none',
                        borderRadius: borderRadius.md,
                        cursor: isApplyingReports ? 'not-allowed' : 'pointer',
                        fontSize: typography.small.fontSize,
                        fontWeight: typographyFlat.fontWeight.medium,
                        alignSelf: 'flex-start',
                      }}
                    >
                      {isApplyingReports ? 'Applying…' : 'Apply Selected Report Lines'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {order.source.toLowerCase() === 'manapool' && (
            <CombinePackageSection
              order={order}
              onOrderUpdated={onOrderUpdated}
              onOpenPartner={onOpenPartner}
              onActiveChange={setCombineWide}
              client={combineClient}
            />
          )}

          {/* Order Total */}
          <div style={{
            backgroundColor: colors.backgroundAlt,
            border: `1px solid ${colors.border}`,
            borderRadius: borderRadius.lg,
            padding: spacing.lg,
            marginBottom: spacing.lg,
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: typography.small.fontSize,
              color: colors.textSecondary,
              marginBottom: spacing.xs
            }}>
              Order Total
            </div>
            <div style={{
              fontSize: typography.h1.fontSize,
              fontWeight: typography.h1.fontWeight,
              color: colors.text
            }}>
              {formatAmount(order.total)}
            </div>
            <div style={{
              marginTop: spacing.md,
              textAlign: 'left',
              fontSize: typography.small.fontSize,
              color: colors.textSecondary,
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: `${spacing.xs} ${spacing.md}`,
            }}>
              <span>Subtotal</span>
              <span>{formatAmount(order.subtotal)}</span>
              <span>Shipping</span>
              <span>{formatAmount(order.shipping)}</span>
              <span>Taxes</span>
              <span>{formatAmount(order.taxes)}</span>
              <span>Fees</span>
              <span>{formatFees()}</span>
              {profitView && (
                <>
                  <span>COGS</span>
                  <span>{formatAmount(profitView.cogs)}</span>
                  <span>Gross Profit</span>
                  <span>{formatAmount(profitView.gross_profit)}</span>
                  <span>Gross Margin</span>
                  <span>
                    {profitView.gross_margin_pct === null || profitView.gross_margin_pct === undefined
                      ? '—'
                      : `${Number(profitView.gross_margin_pct).toFixed(1)}%`}
                  </span>
                  <span>Net Profit</span>
                  <span>{formatAmount(profitView.net_profit)}</span>
                </>
              )}
            </div>
            {profitView && profitView.cogs_complete === false && (
              <div style={{
                marginTop: spacing.sm,
                fontSize: typography.small.fontSize,
                color: colors.textSecondary,
                textAlign: 'left',
              }}>
                COGS incomplete — some lines are short or unallocated
                {order.source.toLowerCase() === 'manapool'
                  ? ' (fetch Manapool reports for line details when available).'
                  : '.'}
                {onEstimateIncompleteCogs && (
                  <div style={{ marginTop: spacing.sm }}>
                    <button
                      type="button"
                      disabled={estimateBusy}
                      onClick={async () => {
                        try {
                          setEstimateBusy(true);
                          setEstimateError(null);
                          setEstimateMessage(null);
                          await onEstimateIncompleteCogs();
                          setEstimateMessage('Estimated COGS applied for incomplete lines.');
                          onAfterUpdate?.();
                        } catch (err) {
                          setEstimateError(
                            err instanceof Error ? err.message : 'Failed to estimate COGS',
                          );
                        } finally {
                          setEstimateBusy(false);
                        }
                      }}
                      style={{
                        padding: `${spacing.xs} ${spacing.md}`,
                        backgroundColor: colors.white,
                        color: colorExtensions.warningDark,
                        border: `1px solid ${colors.warning}`,
                        borderRadius: borderRadius.md,
                        cursor: estimateBusy ? 'not-allowed' : 'pointer',
                        fontSize: typography.small.fontSize,
                      }}
                    >
                      {estimateBusy ? 'Estimating…' : 'Estimate missing COGS from sale price'}
                    </button>
                    {estimateMessage && (
                      <div style={{ marginTop: spacing.xs, color: colorExtensions.successDark }}>
                        {estimateMessage}
                      </div>
                    )}
                    {estimateError && (
                      <div style={{ marginTop: spacing.xs, color: colorExtensions.errorDark }}>
                        {estimateError}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {profitError && (
              <div style={{
                marginTop: spacing.sm,
                fontSize: typography.small.fontSize,
                color: '#b00020',
                textAlign: 'left',
              }}>
                {profitError}
              </div>
            )}
            <div style={{
              fontSize: typography.small.fontSize,
              color: colors.textSecondary,
              marginTop: spacing.xs
            }}>
              {order.item_count} {order.item_count === 1 ? 'item' : 'items'}
            </div>
          </div>

          {/* Line Items */}
          <div>
            <h3 style={{
              fontSize: typography.body.fontSize,
              fontWeight: typographyFlat.fontWeight.semibold,
              color: colors.text,
              margin: 0,
              marginBottom: spacing.md
            }}>
              Line Items
            </h3>

            {isLoading ? (
              <div style={{
                padding: spacing.lg,
                textAlign: 'center',
                color: colors.textSecondary,
                fontSize: typography.body.fontSize
              }}>
                Loading line items...
              </div>
            ) : lineItems.length === 0 ? (
              <div style={{
                padding: spacing.lg,
                textAlign: 'center',
                color: colors.textSecondary,
                fontSize: typography.body.fontSize
              }}>
                No line items found
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: selectedLineItem ? '1.6fr 1fr' : '1fr',
                gap: spacing.md,
              }}>
                <div style={{
                  border: `1px solid ${colors.border}`,
                  borderRadius: borderRadius.lg,
                  overflow: 'hidden',
                }}>
                  {lineItems.map((item, idx) => {
                    const active = selectedLineItem?.id === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => handleSelectLineItem(item)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          border: 'none',
                          padding: spacing.md,
                          borderBottom: idx < lineItems.length - 1 ? `1px solid ${colors.border}` : 'none',
                          backgroundColor: active ? `${colors.primary}12` : colors.white,
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: spacing.sm,
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontSize: typography.body.fontSize,
                              fontWeight: typographyFlat.fontWeight.medium,
                              color: colors.text,
                              marginBottom: spacing.xs,
                            }}>
                              {item.product_name || <span style={{ color: colors.textSecondary, fontStyle: 'italic' }}>Product name unavailable</span>}
                            </div>
                            {item.external_line_id && (
                              <div style={{
                                fontSize: typography.small.fontSize,
                                color: colors.textSecondary,
                                fontFamily: 'monospace',
                              }}>
                                Line ID: {item.external_line_id}
                              </div>
                            )}
                          </div>
                          <div style={{
                            fontSize: typography.body.fontSize,
                            fontWeight: typographyFlat.fontWeight.semibold,
                            color: colors.text,
                            textAlign: 'right',
                          }}>
                            {formatAmount(item.line_total)}
                          </div>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: typography.small.fontSize,
                          color: colors.textSecondary,
                        }}>
                          <span>Qty: {item.requested_qty}</span>
                          {item.shortfall_qty > 0 ? <span>Shortfall: {item.shortfall_qty}</span> : <span>Allocated: {item.allocated_qty}</span>}
                          <span>Unit: {formatAmount(item.unit_price)}</span>
                        </div>
                        {item.cogs !== undefined && item.cogs !== null && (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: typography.small.fontSize,
                            color: colors.textSecondary,
                            marginTop: spacing.xs,
                          }}>
                            <span>COGS: {formatAmount(item.cogs)}</span>
                            <span>Profit: {formatAmount(item.gross_profit)}</span>
                            <span>
                              {item.gross_margin_pct === null || item.gross_margin_pct === undefined
                                ? ''
                                : `${Number(item.gross_margin_pct).toFixed(0)}%`}
                            </span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {selectedLineItem && (
                  <div style={{
                    border: `1px solid ${colors.border}`,
                    borderRadius: borderRadius.lg,
                    padding: spacing.md,
                    backgroundColor: colors.white,
                  }}>
                    <h4 style={{
                      margin: 0,
                      marginBottom: spacing.sm,
                      fontSize: typography.body.fontSize,
                      fontWeight: typographyFlat.fontWeight.semibold,
                      color: colors.text,
                    }}>
                      Card Details
                    </h4>
                    <div style={{ fontSize: typography.small.fontSize, color: colors.textSecondary, marginBottom: spacing.sm }}>
                      {selectedLineItem.product_name || 'Unknown card'}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: `${spacing.xs} ${spacing.sm}`, fontSize: typography.small.fontSize }}>
                      <span style={{ color: colors.textSecondary }}>Condition</span>
                      <strong>{selectedLineItem.condition || '—'}</strong>
                      <span style={{ color: colors.textSecondary }}>Finish</span>
                      <strong>{selectedLineItem.finish || '—'}</strong>
                      <span style={{ color: colors.textSecondary }}>Requested</span>
                      <strong>{selectedLineItem.requested_qty}</strong>
                      <span style={{ color: colors.textSecondary }}>Allocated</span>
                      <strong>{selectedLineItem.allocated_qty}</strong>
                      <span style={{ color: colors.textSecondary }}>Shortfall</span>
                      <strong>{selectedLineItem.shortfall_qty}</strong>
                    </div>

                    <div style={{ marginTop: spacing.md }}>
                      <div style={{
                        fontSize: typography.small.fontSize,
                        color: colors.textSecondary,
                        marginBottom: spacing.xs,
                      }}>
                        Allocated Copies ({selectedLineItem.allocated_copies?.length || 0})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs, maxHeight: '220px', overflowY: 'auto' }}>
                        {(selectedLineItem.allocated_copies || []).map((copy) => (
                          <div
                            key={copy.id}
                            style={{
                              border: `1px solid ${colors.border}`,
                              borderRadius: borderRadius.sm,
                              padding: spacing.xs,
                              fontSize: typography.small.fontSize,
                            }}
                          >
                            <div style={{ fontFamily: 'monospace', marginBottom: spacing.xs }}>{copy.id}</div>
                            <div style={{ color: colors.textSecondary }}>
                              {copy.condition || '—'} • {copy.printing || (copy.is_foil ? 'foil' : 'normal')} • {copy.language || 'EN'} • {copy.status || '—'}
                            </div>
                            <div style={{ color: colors.textSecondary }}>
                              Bin: {copy.location_bin || '—'} • Cost:{' '}
                              {formatCopyCurrency(
                                copy.unit_cost != null && copy.unit_cost !== undefined
                                  ? copy.unit_cost
                                  : copy.acquisition_cost,
                              )}
                              {copy.unit_cost != null
                                && copy.acquisition_cost != null
                                && Number(copy.unit_cost) !== Number(copy.acquisition_cost)
                                ? ` (live ${formatCopyCurrency(copy.acquisition_cost)})`
                                : ''}
                            </div>
                          </div>
                        ))}
                        {(!selectedLineItem.allocated_copies || selectedLineItem.allocated_copies.length === 0) && (
                          <div style={{ fontSize: typography.small.fontSize, color: colors.textSecondary }}>
                            No allocated copy details available.
                          </div>
                        )}
                      </div>
                    </div>

                    {canRetryShortfall && onRetryShortfall && selectedLineItem.shortfall_qty > 0 && (
                      <div style={{
                        marginTop: spacing.md,
                        paddingTop: spacing.md,
                        borderTop: `1px solid ${colors.border}`,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: spacing.xs,
                      }}>
                        <div style={{ fontSize: typography.small.fontSize, color: colors.textSecondary }}>
                          Rematch this shortfall line only (SyncBase + channel qty for this card).
                        </div>
                        {retryError && (
                          <div style={{ fontSize: typography.small.fontSize, color: colors.error }}>
                            {retryError}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={handleRetryShortfall}
                          disabled={isSubmittingRetry}
                          style={{
                            padding: `${spacing.xs} ${spacing.sm}`,
                            border: `1px solid ${colors.secondary}`,
                            borderRadius: borderRadius.sm,
                            backgroundColor: colors.white,
                            color: colors.secondary,
                            fontSize: typography.small.fontSize,
                            cursor: isSubmittingRetry ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {isSubmittingRetry ? 'Retrying…' : 'Retry shortfall allocation'}
                        </button>
                      </div>
                    )}

                    {canMarkMissing && onMarkMissing && (
                      <div style={{
                        marginTop: spacing.md,
                        paddingTop: spacing.md,
                        borderTop: `1px solid ${colors.border}`,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: spacing.xs,
                      }}>
                        <div style={{ fontSize: typography.small.fontSize, color: colors.textSecondary }}>
                          Mark Missing (Pending Manapool)
                        </div>
                        <label style={{ fontSize: typography.small.fontSize, color: colors.textSecondary }}>Quantity Found</label>
                        <input
                          type="number"
                          min={0}
                          max={selectedLineItem.requested_qty}
                          value={quantityFoundInput}
                          onChange={(event) => setQuantityFoundInput(parseInt(event.target.value || '0', 10))}
                          style={{
                            padding: `${spacing.xs} ${spacing.sm}`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            fontSize: typography.small.fontSize,
                          }}
                        />
                        <label style={{ fontSize: typography.small.fontSize, color: colors.textSecondary }}>Notes (optional)</label>
                        <textarea
                          value={missingNotes}
                          onChange={(event) => setMissingNotes(event.target.value)}
                          rows={3}
                          style={{
                            padding: `${spacing.xs} ${spacing.sm}`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            fontSize: typography.small.fontSize,
                            resize: 'vertical',
                          }}
                        />
                        {missingError && (
                          <div style={{ fontSize: typography.small.fontSize, color: colors.error }}>
                            {missingError}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={handleMarkMissing}
                          disabled={isSubmittingMissing}
                          style={{
                            padding: `${spacing.xs} ${spacing.sm}`,
                            border: `1px solid ${colors.error}`,
                            borderRadius: borderRadius.sm,
                            backgroundColor: colors.white,
                            color: colors.error,
                            fontSize: typography.small.fontSize,
                            cursor: isSubmittingMissing ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {isSubmittingMissing ? 'Saving...' : 'Mark Missing'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
